
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { debounceTime, filter, Subject, take, takeUntil } from 'rxjs';
import { FileDownloadUtility } from '../../../../../shared/utilities/blob-download-utility';
import { DocumentService } from '../../../../sa-library/shared/services/document.service';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { materialSymbols } from '../../../../../shared/constants/material-symbols';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IApiResponse } from '../../../../../shared/models/api-response';
import { IBlobFile } from '../../../../../shared/models/blob-file';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { urlUtility } from '../../../../../shared/utilities/url-utility';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { DateFormatPipe } from '../../../../controls/pipes/date-format.pipe';
import { SummaryComponentView } from '../../../shared/components/button-vote-now/button-vote-now.component';
import { IIecBallotDetails } from '../../../shared/models/iec/iec-ballot-details';
import { IecBallotStatus } from '../../../shared/models/iec/iec-ballot-summary';
import { IIecBallotVote, IecVoteId, iecVoteOption } from '../../../shared/models/iec/iec-ballot-vote';
import { IUpdateIecBallotDatesRequest, IUpdateIecBallotNoteRequest } from '../../../shared/models/iec/iec-update-request';
import { IecBallotService } from '../../../shared/services/iec-ballot.service';
import { IecPlaceVoteComponent } from '../../iec-place-vote/iec-place-vote.component';

@Component({
    selector: 'app-iec-summary',
    templateUrl: './iec-summary.component.html',
    styleUrl: './iec-summary.component.scss',
    standalone: false
})
export class IecSummaryComponent implements OnInit {
  @Input() iecBallotDetail: IIecBallotDetails | null = null;

  @Output() onPlaceVote = new EventEmitter<string>();

  isBallotStartDateEditable: boolean = false;
  isBallotEndDateEditable: boolean = false;
  ballotStartDate: Date | undefined = undefined;
  ballotEndDate: Date | undefined = undefined;
  ballotStartMinDate: Date = new Date();
  ballotStartMaxDate: Date = new Date();
  ballotEndMinDate: Date = new Date();
  ballotEndMaxDate: Date = new Date();
  ballotId: string = '';
  ballotSummaryDateForm: FormGroup;
  note: string = '';
  isDisabled: boolean = true;
  currentUser: IUser | null = null;
  isUserPmOrAbove: boolean = false;
  isAuthorized: boolean = false;
  ballotStatusId: UnifiedBallotStatus = UnifiedBallotStatus.created;

  userVote: IIecBallotVote | undefined;
  ballotVotes: IIecBallotVote[] = [];
  isJoint: boolean = false;
  isVoteButtonEnable: boolean = false;
  hasVoted: boolean = false;
  isVotingActionsVisible: boolean = false;
  isInternalPublicUser: boolean = false;

  withdraw = materialSymbols.logout;
  reconfirm = materialSymbols.checkCircle;
  revise = materialSymbols.refresh;

  help = materialSymbols.help

  userVoteOption: any;
  userVoteSubmittedDate: string | undefined;

  draftFile!: IBlobFile;
  commentTemplate!: IBlobFile;

  private ballotNoteSubject: Subject<string> = new Subject();
  private unsubscribe$ = new Subject<void>();

  openVotePopup: boolean = false;

  maxLengthToDisplay : number = 75;

  constructor(
    public _authService: AuthService,
    private _iecBallotService: IecBallotService,
    private _documentService: DocumentService,
    private _samlService: SamlService,
    private _modalService: NgbModal,
    private _activeRouter: ActivatedRoute,
    private _router: Router,
    private fb: FormBuilder,
    private dateFormatPipe: DateFormatPipe
  ) {
    this.checkUserPermission();
    this.ballotNoteSubject.pipe(debounceTime(1000)).subscribe((note) => {
      this.note = note;
      this.updateBallotNote(note);
    });
    this.ballotSummaryDateForm = this.fb.group({
      ballotStartDateCtrl: new FormControl(),
      ballotEndDateCtrl: new FormControl(),
    });
  }

  ngOnInit() {
    this.checkDatesEditableStatus();
    this.detectBallotStartDateChanges();
    this.detectBallotEndDateChanges();

    this.getDraft();
    this.getCommentTemplate();

    this.setViewRedirection();

    this.initVoteButton();

  }

  initializeBallotDates(scheduledBallot = false) {
    const today = new Date();
    this.ballotStartDate =
      this.iecBallotDetail?.ballotSummary?.startDate || undefined;
    this.ballotEndDate =
      this.iecBallotDetail?.ballotSummary?.endDate || undefined;
    if (scheduledBallot && this.ballotStartDate && this.ballotEndDate) {
      this.ballotSummaryDateForm.patchValue({
        ballotStartDateCtrl: this.ballotStartDate,
        ballotEndDateCtrl: this.ballotEndDate,
      });
      this.ballotStartMinDate = new Date(today);
      this.ballotStartMaxDate = new Date(this.ballotEndDate);
      this.ballotStartMaxDate.setDate(this.ballotStartMaxDate.getDate() - 1);
      this.ballotEndMinDate = new Date(this.ballotStartDate);
      this.ballotEndMinDate.setDate(this.ballotEndMinDate.getDate() + 1);
    }
    if (!scheduledBallot && this.ballotStartDate && this.ballotEndDate) {
      this.ballotSummaryDateForm.patchValue({
        ballotEndDateCtrl: this.ballotEndDate,
      });

      this.ballotEndMinDate = new Date(today);
      this.ballotEndMinDate.setDate(today.getDate() - 1);
    }
  }

  checkUserPermission() {
    this._authService.currentUserSubject
    .pipe(
      filter(user => !!user),
      take(1)
    )
    .subscribe((user) => {
      this.currentUser = user;
      if (this.currentUser) {
        this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
        this.isInternalPublicUser = UserUtility.isInternalPublicUser(this.currentUser);
        if (this.isUserPmOrAbove) {
          this.isAuthorized = true;
        } else {
          this._samlService
            .getLoggedInUserHoSDStatus()
            .subscribe((response) => {
              this.isAuthorized = response.content;
            });
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['iecBallotDetail']) {
      this.note = this.iecBallotDetail?.ballotSummary?.notes || '';
      this.ballotStatusId =
      IecBallotStatus[this.iecBallotDetail?.ballotSummary?.statusName??'']
      this.initVoteButton();
    }
  }

  checkDatesEditableStatus() {

    if (this.ballotStatusId && this.isAuthorized) {
      if (this.ballotStatusId == UnifiedBallotStatus.scheduled) {
        this.isBallotStartDateEditable = true;
        this.isBallotEndDateEditable = true;
        this.initializeBallotDates(true);
      }
      else if (this.ballotStatusId == UnifiedBallotStatus.openForVoting) {
        this.isBallotEndDateEditable = true;
        this.initializeBallotDates();
      }
    }
  }

  onInput(event: Event): void {
    const note = (event.target as HTMLInputElement).value;
    this.ballotNoteSubject.next(note);
  }

  updateBallotDates(): void {
    let updateBallotDates: IUpdateIecBallotDatesRequest = {
      ballotId: this.iecBallotDetail?.id??'',
      startDate: this.ballotStartDate,
      endDate: this.ballotEndDate
    }
    this._iecBallotService
      .updateBallotDates(updateBallotDates)
      .subscribe(
        (response) => {
          if (response.success) {
            console.log('Ballot details updated successfully:', response);
          }
        },
        (error) => {
          console.error('Error updating ballot details:', error);
        }
      );
  }

  updateBallotNote(note: string): void {
    let updateNoteBallot: IUpdateIecBallotNoteRequest  = {
      ballotId: this.iecBallotDetail?.id??'',
      notes: this.note
    }

    this._iecBallotService
      .updateBallotNotes(updateNoteBallot)
      .subscribe(
        (response) => {
          if (response.success) {
            console.log('Ballot details updated successfully:', response);
          }
        },
        (error) => {
          console.error('Error updating ballot details:', error);
        }
      );
  }

  getCommitteeName() {
    return `${this.iecBallotDetail?.ballotSummary?.committeeCode} ${this.iecBallotDetail?.ballotSummary?.committee}`;
  }

  detectBallotStartDateChanges(): void {
    this.ballotSummaryDateForm
      .get('ballotStartDateCtrl')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedEndDate) => {
        if (selectedEndDate) {
          this.ballotEndMinDate = new Date(selectedEndDate);
          this.ballotEndMinDate.setDate(this.ballotEndMinDate.getDate() + 1);
          this.ballotStartDate = selectedEndDate;
          this.updateBallotDates();
        }
      });
  }

  detectBallotEndDateChanges(): void {
    this.ballotSummaryDateForm
      .get('ballotEndDateCtrl')
      ?.valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((selectedEndDate) => {
        if (selectedEndDate) {
          this.ballotStartMaxDate = new Date(selectedEndDate);
          this.ballotStartMaxDate.setDate(
            this.ballotStartMaxDate.getDate() - 1
          );
          this.ballotEndDate = selectedEndDate;
          this.updateBallotDates();
        }
      });
  }

  getDraft() {
    this._iecBallotService
      .getDraftFiles(this.iecBallotDetail?.id??'')
      .subscribe({
        next: (response: IApiResponse<IBlobFile>) => {
          if (response.success && response.content) {
            this.draftFile = response.content;          }
        },
        error: (error) => {
          // Log to sumologic
        },
      });
  }

  getCommentTemplate() {
    this._iecBallotService
      .getCommentTemplateFiles(this.iecBallotDetail?.id??'')
      .subscribe({
        next: (response: IApiResponse<IBlobFile>) => {
          if (response.success && response.content) {
            this.commentTemplate = response.content;
          }
        },
        error: (error) => {
          // Log to sumologic
        },
      });
  }

  downloadDraft() {
    FileDownloadUtility.downloadBlobToBrowser(this.draftFile);
  }

  downloadCommentTemplate() {
    FileDownloadUtility.downloadBlobToBrowser(this.commentTemplate);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  truncate(str?: string): string {
    return str?.length??0 > this.maxLengthToDisplay ? str?.slice(0, this.maxLengthToDisplay - 3) + '...' : str??'-';
  }

  initVoteButton() {
    this.userVote = this.iecBallotDetail?.iecBallotVotes.find(vote => vote.email == this.currentUser?.email);

    this.redirectToVoteIfRequired();

    if (!this.userVote) return;

    this.hasVoted = this.userVote?.iecVoteOptionName != IecVoteId.NO_RESPONSE;
    this.isVoteButtonEnable = this.ballotStatusId == UnifiedBallotStatus.openForVoting;
    this.userVoteOption = iecVoteOption[this.userVote?.iecVoteOptionName??""];
    this.userVoteSubmittedDate = this.dateFormatPipe.transform(this.userVote?.lastUpdated);
    this.isVotingActionsVisible = this.userVote?.isEligible??false;


  }

  setViewRedirection() {
    const voteView = this._activeRouter?.snapshot?.queryParamMap?.get(
      SummaryComponentView.vote
    );
    if (voteView) {
      this.openVotePopup = true;
      urlUtility.removeQueryParam(this._router, SummaryComponentView.vote);
    }
  }

  redirectToVoteIfRequired() {

    if (this.openVotePopup && this.userVote) {
      this.openVotePopup = false;
      this.openPlaceVoteModal();
    }
  }


  refreshVoteSection(voteOption: string) {
    this.hasVoted = true;
    this.userVoteOption = iecVoteOption[voteOption];
    this.userVoteSubmittedDate = this.dateFormatPipe.transform(new Date());
  }

  openPlaceVoteModal() {
    const modalRef = this._modalService.open(IecPlaceVoteComponent,  { windowClass: 'place-vote-custom-modal', centered: true});
    modalRef.componentInstance.userVote = this.userVote;
    modalRef.componentInstance.user = this.currentUser;


    modalRef.result.then((result) => {
      console.log('Modal closed with:', result);
    }, (vote) => {
      if(vote) {
        this.refreshVoteSection(vote);
        this.onPlaceVote?.emit(this.iecBallotDetail?.id);
      }
    });
  }
}
